import * as THREE from 'three';
import type { AxialCoord, HexTile } from '@/types/hex.types';
import { HexGrid } from './HexGrid';
import { axialToPixel, coordToKey, mirrorCoord } from './HexCoord';
import { COLORS, HEX_SIZE, GRID_WIDTH } from '@/utils/constants';

/**
 * Renders hex tiles using Three.js
 */
export class HexRenderer {
  private scene: THREE.Scene;
  private hexGrid: HexGrid;
  private hexMeshes: Map<string, THREE.Mesh> = new Map();
  private originalMaterials: Map<string, THREE.Material> = new Map();
  private hexGroup: THREE.Group;
  private geometry: THREE.BufferGeometry;
  private materials: Map<string, THREE.Material> = new Map();
  private flashingHexes: Set<string> = new Set();
  private placementHexes: Set<string> = new Set();

  // Mirroring state for multiplayer (local player always at bottom)
  private mirrorEnabled = false;
  private localPlayerId: 1 | 2 | null = null;

  constructor(scene: THREE.Scene, hexGrid: HexGrid) {
    this.scene = scene;
    this.hexGrid = hexGrid;
    this.hexGroup = new THREE.Group();
    this.scene.add(this.hexGroup);

    this.geometry = this.createHexGeometry();
    this.createMaterials();
    this.createHexMeshes();
    this.centerGrid();
  }

  /**
   * Create a pointy-top hexagon geometry
   */
  private createHexGeometry(): THREE.BufferGeometry {
    const shape = new THREE.Shape();
    const size = HEX_SIZE * 0.96; // Slightly smaller for subtle gaps

    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i - Math.PI / 6; // Pointy-top offset
      const x = size * Math.cos(angle);
      const y = size * Math.sin(angle);

      if (i === 0) {
        shape.moveTo(x, y);
      } else {
        shape.lineTo(x, y);
      }
    }
    shape.closePath();

    const extrudeSettings = {
      depth: 0.08, // Thinner for flatter look
      bevelEnabled: true,
      bevelThickness: 0.03,
      bevelSize: 0.03,
      bevelSegments: 2,
    };

    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    geometry.rotateX(-Math.PI / 2); // Lay flat on XZ plane

    return geometry;
  }

  /**
   * Create a shader material with radial gradient transparency
   * Center is more transparent, edges are more opaque
   * edgeFadeDirection: -1 = fade towards left, 0 = no fade, 1 = fade towards right
   */
  private createGradientMaterial(
    color: number,
    centerOpacity = 0.2,
    edgeOpacity = 1.0,
    brightness = 1.0,
    edgeFadeDirection = 0
  ): THREE.ShaderMaterial {
    const colorObj = new THREE.Color(color);

    return new THREE.ShaderMaterial({
      uniforms: {
        uColor: { value: colorObj },
        uCenterOpacity: { value: centerOpacity },
        uEdgeOpacity: { value: edgeOpacity },
        uHexSize: { value: HEX_SIZE * 0.96 },
        uBrightness: { value: brightness },
        uEdgeFadeDirection: { value: edgeFadeDirection },
      },
      vertexShader: `
        varying vec3 vLocalPosition;
        varying vec3 vNormal;

        void main() {
          vLocalPosition = position;
          vNormal = normalize(normalMatrix * normal);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 uColor;
        uniform float uCenterOpacity;
        uniform float uEdgeOpacity;
        uniform float uHexSize;
        uniform float uBrightness;
        uniform float uEdgeFadeDirection;

        varying vec3 vLocalPosition;
        varying vec3 vNormal;

        void main() {
          // Calculate distance from center in XZ plane (hex lies flat)
          // After rotation, the original XY becomes XZ
          float dist = length(vLocalPosition.xz);

          // Normalize distance by hex size
          float normalizedDist = clamp(dist / uHexSize, 0.0, 1.0);

          // Create exponential radial gradient for opacity
          // Power curve keeps center transparent longer, ramps up sharply at edges
          float curve = pow(normalizedDist, 4.0);
          float opacity = mix(uCenterOpacity, uEdgeOpacity, curve);

          // Apply edge fade if enabled
          // uEdgeFadeDirection: -1 = fade left, 0 = none, 1 = fade right
          if (uEdgeFadeDirection != 0.0) {
            // Normalize X position to -1..1 range
            float normalizedX = vLocalPosition.x / uHexSize;
            // Calculate fade factor: 1.0 at opposite edge, 0.5 at fade edge
            float fadeFactor;
            if (uEdgeFadeDirection < 0.0) {
              // Fade towards left: left side (negative X) becomes more transparent
              fadeFactor = smoothstep(-1.0, 0.5, normalizedX);
            } else {
              // Fade towards right: right side (positive X) becomes more transparent
              fadeFactor = smoothstep(1.0, -0.5, normalizedX);
            }
            // Fade to 50% opacity minimum instead of 0%
            opacity *= mix(0.75, 1.0, fadeFactor);
          }

          // Simple lighting based on normal (facing up = brighter)
          float lighting = 0.6 + 0.4 * max(0.0, vNormal.y);

          vec3 finalColor = uColor * lighting * uBrightness;
          gl_FragColor = vec4(finalColor, opacity);
        }
      `,
      transparent: true,
      side: THREE.DoubleSide,
    });
  }

  /**
   * Create materials - grass green theme
   */
  private createMaterials(): void {
    // Main grass material with radial gradient (transparent with visible edges)
    // Lower brightness to match muted background tones
    this.materials.set(
      'grass',
      this.createGradientMaterial(COLORS.grassGreen, 0.1, 0.4, 0.38)
    );

    // Light grass variation with radial gradient
    this.materials.set(
      'grassLight',
      this.createGradientMaterial(COLORS.grassGreenLight, 0.1, 0.4, 0.4)
    );

    // Dark grass variation with radial gradient
    this.materials.set(
      'grassDark',
      this.createGradientMaterial(COLORS.grassGreenDark, 0.1, 0.4, 0.35)
    );

    // Left edge fade variants (fade towards left) - brightness matches base materials
    this.materials.set(
      'grassLeftEdge',
      this.createGradientMaterial(COLORS.grassGreen, 0.1, 0.4, 0.38, -1)
    );
    this.materials.set(
      'grassLightLeftEdge',
      this.createGradientMaterial(COLORS.grassGreenLight, 0.1, 0.4, 0.4, -1)
    );
    this.materials.set(
      'grassDarkLeftEdge',
      this.createGradientMaterial(COLORS.grassGreenDark, 0.1, 0.4, 0.35, -1)
    );

    // Right edge fade variants (fade towards right) - brightness matches base materials
    this.materials.set(
      'grassRightEdge',
      this.createGradientMaterial(COLORS.grassGreen, 0.1, 0.4, 0.38, 1)
    );
    this.materials.set(
      'grassLightRightEdge',
      this.createGradientMaterial(COLORS.grassGreenLight, 0.1, 0.4, 0.4, 1)
    );
    this.materials.set(
      'grassDarkRightEdge',
      this.createGradientMaterial(COLORS.grassGreenDark, 0.1, 0.4, 0.35, 1)
    );

    // Placement highlight (yellow, less intense) with radial gradient
    this.materials.set(
      'placement',
      this.createGradientMaterial(COLORS.hexHover, 0.15, 0.7)
    );

    // Hover highlight (brighter yellow with more glow) with radial gradient
    this.materials.set(
      'hover',
      this.createGradientMaterial(COLORS.hexHover, 0.4, 1.0, 1.4)
    );

    // Damage flash material (red glow)
    this.materials.set(
      'damage',
      new THREE.MeshStandardMaterial({
        color: 0xff4444,
        roughness: 0.5,
        metalness: 0.1,
        emissive: 0xff0000,
        emissiveIntensity: 0.4,
      })
    );
  }

  /**
   * Get material for a tile - checkerboard grass pattern with edge fade
   * Edge fade alternates by row: even rows fade left edge, odd rows fade right edge
   */
  private getMaterial(tile: HexTile): THREE.Material {
    // Determine column from axial coordinates (offset col = q + floor(r/2))
    const col = tile.coord.q + Math.floor(tile.coord.r / 2);
    const row = tile.coord.r;
    const isLeftEdge = col === 0;
    const isRightEdge = col === GRID_WIDTH - 1;
    const isEvenRow = row % 2 === 0;

    // Create subtle checkerboard pattern with grass colors
    const sum = tile.coord.q + tile.coord.r;
    let baseMaterial: string;
    if (sum % 3 === 0) {
      baseMaterial = 'grassLight';
    } else if (sum % 3 === 1) {
      baseMaterial = 'grass';
    } else {
      baseMaterial = 'grassDark';
    }

    // Apply edge fade variants - alternating by row
    // Even rows: left edge fades, Odd rows: right edge fades
    if (isLeftEdge && isEvenRow) {
      return this.materials.get(baseMaterial + 'LeftEdge')!;
    } else if (isRightEdge && !isEvenRow) {
      return this.materials.get(baseMaterial + 'RightEdge')!;
    }
    return this.materials.get(baseMaterial)!;
  }

  /**
   * Create mesh for each hex tile
   */
  private createHexMeshes(): void {
    const tiles = this.hexGrid.getAllTiles();

    for (const tile of tiles) {
      // Apply mirroring for visual position if enabled
      const visualCoord = this.mirrorEnabled
        ? mirrorCoord(tile.coord)
        : tile.coord;
      const pixel = axialToPixel(visualCoord);
      const material = this.getMaterial(tile);
      const mesh = new THREE.Mesh(this.geometry, material);

      mesh.position.set(pixel.x, 0, pixel.y);
      mesh.receiveShadow = true;
      mesh.castShadow = true;

      // Store both server (original) and visual coordinates
      mesh.userData.serverCoord = tile.coord; // Original server coordinate
      mesh.userData.coord = visualCoord; // Visual coordinate (may be mirrored)
      mesh.userData.key = coordToKey(tile.coord); // Key uses server coord

      this.hexMeshes.set(coordToKey(tile.coord), mesh);
      this.originalMaterials.set(coordToKey(tile.coord), material);
      this.hexGroup.add(mesh);
    }
  }

  /**
   * Center the grid in the scene
   */
  private centerGrid(): void {
    const box = new THREE.Box3().setFromObject(this.hexGroup);
    const center = box.getCenter(new THREE.Vector3());
    this.hexGroup.position.sub(center);
    this.hexGroup.position.y = 0;
  }

  /**
   * Highlight a hex tile on hover (only if it's a placement tile)
   */
  highlightHex(coord: AxialCoord): void {
    const key = coordToKey(coord);
    // Only apply hover highlight to placement tiles
    if (!this.placementHexes.has(key)) return;

    const mesh = this.hexMeshes.get(key);
    if (mesh) {
      mesh.material = this.materials.get('hover')!;
    }
  }

  /**
   * Highlight a hex tile for placement (less intense than hover)
   */
  highlightForPlacement(coord: AxialCoord): void {
    const key = coordToKey(coord);
    const mesh = this.hexMeshes.get(key);
    if (mesh) {
      this.placementHexes.add(key);
      mesh.material = this.materials.get('placement')!;
    }
  }

  /**
   * Remove hover highlight from a hex tile
   * If it's a placement tile, restore to placement material; otherwise restore to original
   */
  unhighlightHex(coord: AxialCoord): void {
    const key = coordToKey(coord);
    const mesh = this.hexMeshes.get(key);
    if (!mesh) return;

    // If it's a placement tile, restore to placement material
    if (this.placementHexes.has(key)) {
      mesh.material = this.materials.get('placement')!;
    } else {
      const originalMaterial = this.originalMaterials.get(key);
      if (originalMaterial) {
        mesh.material = originalMaterial;
      }
    }
  }

  /**
   * Clear placement highlight from a hex tile (restore to original)
   */
  clearPlacementHighlight(coord: AxialCoord): void {
    const key = coordToKey(coord);
    this.placementHexes.delete(key);
    const mesh = this.hexMeshes.get(key);
    const originalMaterial = this.originalMaterials.get(key);
    if (mesh && originalMaterial) {
      mesh.material = originalMaterial;
    }
  }

  /**
   * Clear all placement highlights
   */
  clearAllPlacementHighlights(): void {
    for (const key of this.placementHexes) {
      const mesh = this.hexMeshes.get(key);
      const originalMaterial = this.originalMaterials.get(key);
      if (mesh && originalMaterial) {
        mesh.material = originalMaterial;
      }
    }
    this.placementHexes.clear();
  }

  /**
   * Flash a hex tile to indicate damage
   */
  flashDamage(coord: AxialCoord, duration: number = 150): void {
    const key = coordToKey(coord);
    const mesh = this.hexMeshes.get(key);
    const originalMaterial = this.originalMaterials.get(key);

    if (!mesh || !originalMaterial) return;

    // Don't start a new flash if already flashing
    if (this.flashingHexes.has(key)) return;

    this.flashingHexes.add(key);
    mesh.material = this.materials.get('damage')!;

    setTimeout(() => {
      // Only restore if still flashing (not interrupted by another effect)
      if (this.flashingHexes.has(key)) {
        mesh.material = originalMaterial;
        this.flashingHexes.delete(key);
      }
    }, duration);
  }

  /**
   * Get all hex meshes for raycasting
   */
  getHexMeshes(): THREE.Mesh[] {
    return Array.from(this.hexMeshes.values());
  }

  /**
   * Get the hex group for scene manipulation
   */
  getGroup(): THREE.Group {
    return this.hexGroup;
  }

  /**
   * Update a specific hex tile's appearance
   */
  updateTile(coord: AxialCoord): void {
    const key = coordToKey(coord);
    const mesh = this.hexMeshes.get(key);
    const originalMaterial = this.originalMaterials.get(key);
    if (mesh && originalMaterial) {
      mesh.material = originalMaterial;
    }
  }

  /**
   * Enable or disable coordinate mirroring for multiplayer
   * When Player 2, we mirror the grid so their territory appears at the bottom
   */
  setMirroring(localPlayerId: 1 | 2 | null, enabled: boolean): void {
    this.localPlayerId = localPlayerId;
    this.mirrorEnabled = enabled;
    this.updateMeshPositions();
  }

  /**
   * Check if mirroring is enabled
   */
  isMirrorEnabled(): boolean {
    return this.mirrorEnabled;
  }

  /**
   * Get the local player ID for mirroring
   */
  getLocalPlayerId(): 1 | 2 | null {
    return this.localPlayerId;
  }

  /**
   * Update all mesh positions based on mirroring state
   */
  private updateMeshPositions(): void {
    const tiles = this.hexGrid.getAllTiles();

    for (const tile of tiles) {
      const key = coordToKey(tile.coord);
      const mesh = this.hexMeshes.get(key);
      if (!mesh) continue;

      // Calculate visual coordinate based on mirroring
      const visualCoord = this.mirrorEnabled
        ? mirrorCoord(tile.coord)
        : tile.coord;
      const pixel = axialToPixel(visualCoord);

      // Update position
      mesh.position.set(pixel.x, 0, pixel.y);

      // Update stored coordinates
      mesh.userData.coord = visualCoord;
      // serverCoord stays the same (original tile.coord)
    }

    // Re-center the grid after position changes
    this.centerGrid();
  }

  /**
   * Clean up resources
   */
  dispose(): void {
    this.geometry.dispose();
    this.materials.forEach((material) => material.dispose());
    this.hexMeshes.clear();
    this.scene.remove(this.hexGroup);
  }
}
